/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import type { TerrificEmbedData } from './loader';
import { useTerrificSdk } from './use-terrific-sdk';
import { useTerrificLoaded } from './use-terrific-loaded';

// oxlint-disable-next-line react-refresh/only-export-components -- loader re-export is required for the static-registry AST plugin
export { loader } from './loader';

@Component('terrificEmbed', {
    name: 'Terrific Embed',
    group: 'priceshoes_content',
    description: 'Shoppable video carousel rendered by Terrific for the configured embedding ID',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class TerrificEmbedMetadata {
    @AttributeDefinition({
        name: 'Embedding ID',
        type: 'string',
        description: 'Carousel ID provided by Terrific (Marketing > Embedded collections)',
    })
    embeddingId!: string;
}

interface TerrificEmbedProps {
    embeddingId?: string;
    /** Loader data: the Store ID from the `terrificStoreId` site preference. */
    data?: TerrificEmbedData;
    className?: string;
}

/**
 * Container for a Terrific embed. Terrific renders the whole carousel (videos, texts, ribbon and
 * button) inside it, so this component only provides the target `div` and loads the SDK.
 *
 * The container is client-only: the SDK mutates it as soon as it is in the DOM, which would break
 * hydration of server-rendered markup. Its children belong to the SDK, so React never renders any.
 */
export default function TerrificEmbed({ embeddingId, data, className }: TerrificEmbedProps) {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    const targetRef = useRef<HTMLDivElement>(null);
    const loaded = useTerrificLoaded(targetRef, mounted);

    const id = embeddingId?.trim();
    const storeId = data?.storeId;
    useTerrificSdk(id ? storeId : undefined);

    if (!id || !storeId) {
        // Authoring affordance only: on the live storefront an unconfigured embed renders nothing.
        if (!isDesignMode) return null;
        return (
            <p data-slot="terrific-embed" className={cn('py-8 text-center text-muted-foreground', className)}>
                {t(id ? 'terrificEmbed.missingStoreId' : 'terrificEmbed.emptyEmbeddingId')}
            </p>
        );
    }

    // The skeleton is server-rendered and overlays the reserved height until Terrific's iframe
    // loads, so the embed doesn't shift the page when it appears.
    return (
        <div
            data-slot="terrific-embed"
            aria-busy={!loaded}
            className={cn('relative w-full', !loaded && 'min-h-[400px]', className)}>
            {/* oxlint-disable-next-line react/no-unknown-property -- `embedding-id` is the attribute the Terrific SDK reads */}
            {mounted && <div key={id} ref={targetRef} data-source="terrific" embedding-id={id} />}
            {!loaded && <TerrificEmbedSkeleton />}
        </div>
    );
}

/** Single block covering the reserved height while Terrific loads. */
function TerrificEmbedSkeleton() {
    return <Skeleton data-slot="terrific-embed-skeleton" aria-hidden="true" className="absolute inset-0" />;
}
